import {createContext, useContext} from 'react'
import PropTypes from 'prop-types'
import {useSimpleQuery} from '@frnt/base/hooks'
import {ResourceApiServiceProvider} from '../resource-api-service-provider'
import qs from 'qs'

const OrganizationsServiceContext = createContext(undefined)

const OrganizationsServiceProvider = ({
  children,
  baseApiHost,
  handleUnauthorized = () => {
    // Do nothing
  }
}) => {
  return (
    <ResourceApiServiceProvider
      Context={OrganizationsServiceContext}
      handleUnauthorized={handleUnauthorized}
      host={baseApiHost}
      valueCallback={(axios) => ({
        create: async (payload) => {
          const {data} = await axios.post(`/api/v1/org/organizations`, payload)
          return data
        },
        list: async (query) => {
          const {data} = await axios.get(
            `/api/v1/org/organizations${qs.stringify(query, {
              addQueryPrefix: true
            })}`
          )
          return data
        },
        get: async (id) => {
          const {data} = await axios.get(`/api/v1/org/organizations/${id}`)
          return data
        },
        update: async (id, payload) => {
          const {data} = await axios.patch(`/api/v1/org/organizations/${id}`, payload)
          return data
        },
        createMemoEntry: async (organizationId, payload) => {
          const {data} = await axios.post(`/api/v1/org/organizations/${organizationId}/memo_entries`, payload)
          return data
        },
        listMemoEntries: async (organizationId, query) => {
          const {data} = await axios.get(
            `/api/v1/org/organizations/${organizationId}/memo_entries${qs.stringify(query, {
              addQueryPrefix: true
            })}`
          )
          return data
        },
        getKyc: async (id, is_refreshing = false) => {
          const {data} = await axios.get(`/api/v1/org/organizations/${id}/kyc?include_refresh=${is_refreshing}`)
          return data
        },
        resendEmail: async (id, payload) => {
          const {data} = await axios.post(`/api/v1/org/organizations/${id}/resend_email`, payload)
          return data
        },
        approveKyc: async ({organizationId, payload, is_refreshing = false}) => {
          const {data} = await axios.post(`/api/v1/org/organizations/${organizationId}/kyc/approve`, {
            ...payload,
            is_refreshing
          })
          return data
        },
        enableKycRefresh: async ({organizationId, payload}) => {
          const {data} = await axios.post(`/api/v1/org/organizations/${organizationId}/kyc/refresh`, payload)
          return data
        },
        submitKyc: async ({organizationId, payload, is_refreshing = false}) => {
          const {data} = await axios.post(`/api/v1/org/organizations/${organizationId}/kyc/submit`, {
            ...payload,
            is_refreshing
          })
          return data
        },
        approveTraders: async ({organizationId, payload}) => {
          const {data} = await axios.patch(`/api/v1/org/organizations/${organizationId}/verification`, payload)
          return data
        },
        updateKyc: async ({organizationId, payload, is_refreshing}) => {
          const {data} = await axios.put(`/api/v1/org/organizations/${organizationId}/kyc?is_refreshing=${is_refreshing}`, payload)
          return data
        },
        listOtcBalances: async (organizationId, query) => {
          const {data} = await axios.get(
            `/api/v1/org/organizations/${organizationId}/balances${qs.stringify(query, {
              addQueryPrefix: true
            })}`
          )
          return data
        },
        listOtcPayments: async (organizationId, query) => {
          const {data} = await axios.get(
            `/api/v1/org/organizations/${organizationId}/payments${qs.stringify(query, {
              addQueryPrefix: true
            })}`
          )
          return data
        }
      })}>
      {children}
    </ResourceApiServiceProvider>
  )
}

OrganizationsServiceProvider.propTypes = {
  children: PropTypes.any,
  baseApiHost: PropTypes.string.isRequired,
  handleUnauthorized: PropTypes.func
}

const useOrganizationsService = () => {
  const context = useContext(OrganizationsServiceContext)
  if (context === undefined) {
    throw new Error('OrganizationsServiceContext was not provided. Make sure your component is a child of the OrganizationsServiceProvider.')
  }
  return context
}

const useOrganizationsQuery = ({query, ...otherProps}) => {
  const organizationsService = useOrganizationsService()
  return useSimpleQuery({
    queryKey: `organizations-list-${JSON.stringify(query)}`,
    queryFn: async () => organizationsService.list(query),
    ...otherProps
  })
}

const useOrganizationQuery = ({id, query, enabled, ...otherProps}) => {
  const organizationsService = useOrganizationsService()
  return useSimpleQuery({
    queryKey: `organizations-get-${id}`,
    queryFn: async () => organizationsService.get(id, query),
    enabled: enabled !== undefined ? enabled : !!id,
    ...otherProps
  })
}

const useOrganizationKycQuery = ({id, include_refresh = false, enabled, ...otherProps}) => {
  const organizationsService = useOrganizationsService()
  return useSimpleQuery({
    queryKey: `organization-kyc-get-${id}-${include_refresh}`,
    queryFn: async () => organizationsService.getKyc(id, include_refresh),
    enabled: enabled !== undefined ? enabled : !!id,
    ...otherProps
  })
}

const useOrganizationMemoEntriesQuery = ({organizationId, query, ...otherProps}) => {
  const organizationsService = useOrganizationsService()
  return useSimpleQuery({
    queryKey: `organization_memo_entries-list-${organizationId}-${JSON.stringify(query)}`,
    queryFn: async () => organizationsService.listMemoEntries(organizationId, query),
    ...otherProps
  })
}

const useOrganizationOtcBalancesQuery = ({organizationId, ...otherProps}) => {
  const organizationsService = useOrganizationsService()
  return useSimpleQuery({
    queryKey: `otc_balances-list-${organizationId}`,
    queryFn: async () => organizationsService.listOtcBalances(organizationId),
    ...otherProps
  })
}

const useOrganizationOtcPaymentsQuery = ({organizationId, ...otherProps}) => {
  const organizationsService = useOrganizationsService()
  return useSimpleQuery({
    queryKey: `otc_payments-list-${organizationId}`,
    queryFn: async () => organizationsService.listOtcPayments(organizationId),
    ...otherProps
  })
}

export {
  OrganizationsServiceProvider,
  useOrganizationsService,
  useOrganizationsQuery,
  useOrganizationQuery,
  useOrganizationKycQuery,
  useOrganizationMemoEntriesQuery,
  useOrganizationOtcBalancesQuery,
  useOrganizationOtcPaymentsQuery
}
